'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProjectDetail {
  title: string;
  category: string;
  industry: string;
  description: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  results: string[];
  timeline: string;
  teamSize: string;
  technologies: string[];
  clientTestimonial?: {
    quote: string;
    author: string;
    position: string;
  };
}

interface ProjectModalProps {
  project: ProjectDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({
  project,
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-card rounded-2xl shadow-interactive max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="sticky top-4 right-4 float-right z-10 w-10 h-10 bg-muted hover:bg-border rounded-full flex items-center justify-center transition-colors duration-300"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={24} className="text-text-primary" />
        </button>

        <div className="p-8">
          <div className="relative h-80 rounded-xl overflow-hidden mb-6">
            <AppImage
              src={project.image}
              alt={project.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                {project.category}
              </span>
              <span className="px-3 py-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full">
                {project.industry}
              </span>
            </div>
          </div>

          <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
            {project.title}
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <Icon name="ClockIcon" size={20} className="text-primary" />
              <div>
                <div className="text-xs text-text-secondary">Timeline</div>
                <div className="font-semibold text-sm text-text-primary">
                  {project.timeline}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <Icon name="UsersIcon" size={20} className="text-primary" />
              <div>
                <div className="text-xs text-text-secondary">Team Size</div>
                <div className="font-semibold text-sm text-text-primary">
                  {project.teamSize}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-3 flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={20} className="text-warning" />
              The Challenge
            </h3>
            <p className="text-text-secondary leading-relaxed">
              {project.challenge}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-3 flex items-center gap-2">
              <Icon name="LightBulbIcon" size={20} className="text-accent" />
              The Solution
            </h3>
            <p className="text-text-secondary leading-relaxed">
              {project.solution}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-3 flex items-center gap-2">
              <Icon name="ChartBarIcon" size={20} className="text-success" />
              Results & Impact
            </h3>
            <ul className="space-y-2">
              {project.results.map((result, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-text-secondary"
                >
                  <Icon
                    name="CheckCircleIcon"
                    size={20}
                    className="text-success flex-shrink-0 mt-0.5"
                  />
                  <span>{result}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-3 flex items-center gap-2">
              <Icon name="WrenchScrewdriverIcon" size={20} className="text-secondary" />
              Technologies & Methods
            </h3>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-surface text-text-primary text-sm rounded-lg border border-border"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {project.clientTestimonial && (
            <div className="bg-muted rounded-xl p-6 border-l-4 border-primary">
              <Icon
                name="ChatBubbleLeftRightIcon"
                size={24}
                className="text-primary mb-3"
              />
              <p className="text-text-primary italic mb-4 leading-relaxed">
                "{project.clientTestimonial.quote}"
              </p>
              <div>
                <div className="font-semibold text-text-primary">
                  {project.clientTestimonial.author}
                </div>
                <div className="text-sm text-text-secondary">
                  {project.clientTestimonial.position}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;